/**
 * Calendar grid cells, resolved icons, and subscription progress. Used by CalendarScreen.
 */
import moment from 'moment';

export interface CalendarDay {
  date: number | null;
  isAvailable: boolean;
  isFrozen: boolean;
  /** Backend-paused particular day (`status === 'paused'`); distinct from user freeze. */
  isPaused: boolean;
  isDayOff: boolean;
  isToday: boolean;
  fullDate: string;
  icon?: string;
  state?: string;
  /** Anchor day is in subscription `double_box_dates`. */
  isDoubleBox?: boolean;
}

export function isSameDayContent(a: CalendarDay, b: CalendarDay): boolean {
  if (a.fullDate !== b.fullDate || a.date !== b.date) {
    return false;
  }
  return (
    a.state === b.state &&
    a.icon === b.icon &&
    a.isFrozen === b.isFrozen &&
    a.isPaused === b.isPaused &&
    a.isAvailable === b.isAvailable &&
    a.isDayOff === b.isDayOff &&
    a.isToday === b.isToday &&
    Boolean(a.isDoubleBox) === Boolean(b.isDoubleBox)
  );
}

export interface DateIconTheme {
  textLight?: string;
  surface?: string;
  warning?: string;
  alert?: string;
  success?: string;
  primary?: string;
  warningLight?: string;
}

export type DateButtonIconResult = { name: string; color: string };

const FA_STYLE_PREFIXES = new Set([
  'fa-solid',
  'fa-regular',
  'fa-light',
  'fa-thin',
  'fa-duotone',
  'fa-brands',
  'fa',
]);

/**
 * Maps Font Awesome–style class strings (e.g. `"fa fa-truck"`, `"fa-solid fa-check-circle"`)
 * to SvgIcon names. Picks the real glyph token (`fa-*`) and ignores style prefixes.
 */
export function getIconFromApiFormat(icon: string): string | null {
  if (typeof icon !== 'string' || !icon.trim()) {
    return null;
  }
  const tokens = icon.trim().split(/\s+/).filter(Boolean);
  const glyph = [...tokens].reverse().find(t => t.startsWith('fa-') && !FA_STYLE_PREFIXES.has(t));
  if (!glyph) {
    return null;
  }

  const icon_map: Record<string, string> = {
    'fa-ban': 'x',
    'fa-times': 'x',
    'fa-times-circle': 'x',
    'fa-truck': 'driver',
    'fa-star': 'star',
    'fa-pause': 'freeze',
    'fa-snowflake': 'snow',
    'fa-check': 'check',
    'fa-check-circle': 'check',
    'fa-plate-utensils': 'check',
    'fa-circle': 'package',
    'fa-plus': 'plus',
    'fa-plus-circle': 'plus',
    'fa-utensils': 'plus',
  };

  return icon_map[glyph] ?? null;
}

/** True when API icon string means the day should not respond to press (ban / pause / snow). */
export function calendarApiIconBlocksInteraction(icon: string | undefined): boolean {
  if (!icon || typeof icon !== 'string') {
    return false;
  }
  const s = icon.toLowerCase();
  return (
    s.includes('fa-ban') ||
    s.includes('fa-pause') ||
    s.includes('pause') ||
    s.includes('snowflake') ||
    s.includes('fa-times')
  );
}

export function calendarApiIconIsStar(icon: string | undefined): boolean {
  return typeof icon === 'string' && icon.toLowerCase().includes('star');
}

/**
 * Build calendar grid for a month. First row may be null padding cells.
 * Pass **`todayYmd`** (e.g. `formatTodayForApi()`) so “today” updates when the month data is recomputed.
 * Keys must be ASCII `YYYY-MM-DD` — never `moment().format('YYYY-MM-DD')` under an Arabic Moment locale.
 */
export function generateMonthData(
  month: number,
  year: number,
  todayYmd: string,
  freeze_dates: string[],
  pause_dates: string[],
  is_date_day_off: (d: string) => boolean,
  is_date_available: (d: string) => boolean,
  get_date_state: (d: string) => string | undefined,
  get_date_icon?: (d: string) => string | undefined,
): CalendarDay[] {
  const freeze_set = new Set(freeze_dates);
  const pause_set = new Set(pause_dates);
  const start = moment({ year, month, date: 1 });
  const days_in_month = start.daysInMonth();
  const first_weekday = start.day();

  const cells: CalendarDay[] = [];

  for (let i = 0; i < first_weekday; i++) {
    cells.push({
      date: null,
      isAvailable: false,
      isFrozen: false,
      isPaused: false,
      isDayOff: false,
      isToday: false,
      fullDate: '',
    });
  }

  for (let d = 1; d <= days_in_month; d++) {
    const full_date = moment({ year, month, date: d }).locale('en').format('YYYY-MM-DD');
    const is_frozen = freeze_set.has(full_date);
    const is_paused = pause_set.has(full_date);
    const is_day_off = is_date_day_off(full_date);
    const is_available = is_date_available(full_date);
    const state = get_date_state(full_date);
    const icon = get_date_icon?.(full_date);

    cells.push({
      date: d,
      isAvailable: is_available,
      isFrozen: is_frozen,
      isPaused: is_paused,
      isDayOff: is_day_off,
      isToday: full_date === todayYmd,
      fullDate: full_date,
      state,
      ...(icon ? { icon } : {}),
    });
  }

  return cells;
}

export interface GetDateIconOptions {
  /** When true and day is today, show driver icon with primary color (user has active plan). */
  today_with_active_plan?: boolean;
}

function tintForMappedApiIcon(icon_raw: string, mapped: string, theme: Required<DateIconTheme>): DateButtonIconResult {
  const low = icon_raw.toLowerCase();
  if (mapped === 'check') {
    return { name: 'check', color: theme.success };
  }
  if (mapped === 'x' || low.includes('fa-ban') || low.includes('fa-times')) {
    return { name: mapped, color: theme.textLight };
  }
  if (mapped === 'driver' || low.includes('truck')) {
    return { name: mapped, color: theme.surface };
  }
  if (mapped === 'star' || low.includes('star')) {
    return { name: mapped, color: theme.warningLight };
  }
  if (mapped === 'plus' || low.includes('utensils')) {
    return { name: mapped, color: theme.primary };
  }
  if (mapped === 'package' || low.includes('circle')) {
    return { name: mapped, color: theme.warning };
  }
  if (low.includes('pause')) {
    return { name: mapped, color: theme.warning };
  }
  if (low.includes('snow') || low.includes('snowflake')) {
    return { name: mapped, color: theme.alert };
  }
  return { name: mapped, color: theme.primary };
}

/**
 * Resolved SvgIcon + color. API `day.icon` wins when mappable; otherwise uses `day.state`.
 */
export function getDateIcon(day: CalendarDay, theme: DateIconTheme, options?: GetDateIconOptions): DateButtonIconResult | null {
  const alert = theme.alert ?? '#1976d2';
  const warning = theme.warning ?? '#f57c00';
  const success = theme.success ?? '#388e3c';
  const primary = theme.primary ?? '#35858b';
  const text_light = theme.textLight ?? '#8e8e8e';
  const surface = theme.surface ?? '#ffffff';
  const warning_light = theme.warningLight ?? '#ffb74d';
  const full_theme: Required<DateIconTheme> = {
    alert,
    warning,
    success,
    primary,
    textLight: text_light,
    surface,
    warningLight: warning_light,
  };

  if (day.isToday && options?.today_with_active_plan) {
    return { name: 'driver', color: primary };
  }
  if (day.isFrozen) {
    return { name: 'freeze', color: alert };
  }
  if (day.isPaused) {
    return { name: 'lock', color: alert };
  }
  if (day.icon) {
    const mapped = getIconFromApiFormat(day.icon);
    if (mapped) {
      return tintForMappedApiIcon(day.icon, mapped, full_theme);
    }
  }

  if (day.isDayOff) {
    return { name: 'x', color: text_light };
  }
  if (day.state === 'Booked Meal') {
    return { name: 'check', color: success };
  }
  if (day.state === 'Partially Booked Meal') {
    return { name: 'package', color: warning };
  }
  if (day.state === 'Select Meal' && day.isAvailable) {
    return { name: 'plus', color: primary };
  }
  if (day.state === 'Feedback') {
    return { name: 'star', color: warning_light };
  }
  if (day.state === 'Skipped') {
    return { name: 'x', color: text_light };
  }
  if (day.state === 'On the way') {
    return { name: 'driver', color: primary };
  }
  return null;
}

export function calculateSubscriptionProgress(start: string, end: string, total_days: number): { daysLeft: number; daysElapsed: number; progressPercentage: number } {
  const start_m = moment(start, ['YYYY-MM-DD', 'DD MMMM, YYYY'], true);
  const end_m = moment(end, ['YYYY-MM-DD', 'DD MMMM, YYYY'], true);
  const today = moment().startOf('day');

  if (!start_m.isValid() || !end_m.isValid()) {
    return { daysLeft: 0, daysElapsed: 0, progressPercentage: 0 };
  }

  const total_calendar_days = Math.max(1, end_m.diff(start_m, 'days') + 1);
  const days_elapsed = Math.min(total_calendar_days, Math.max(0, today.diff(start_m, 'days') + 1));
  const days_left = Math.max(0, end_m.diff(today, 'days') + 1);
  const display_total = total_days > 0 ? total_days : total_calendar_days;
  const progress_percentage = display_total > 0 ? Math.min(100, Math.round((days_elapsed / display_total) * 100)) : 0;

  return {
    daysLeft: days_left,
    daysElapsed: days_elapsed,
    progressPercentage: progress_percentage,
  };
}

/** Empty-state copy for v2 calendar menu strip when today has no selected meals. */
export function getDateStatusForDay(
  day: CalendarDay | null,
  t: (key: string) => string,
): { title: string; message: string } {
  if (!day) {
    return { title: t('calendar.noOrdersOnThisDay'), message: t('calendar.noOrdersOnThisDayMessage') };
  }
  if (day.isDayOff) {
    return { title: t('calendar.offDay'), message: t('calendar.noOrdersOnThisDayMessage') };
  }
  if (day.isFrozen) {
    return { title: t('calendar.frozenDay'), message: t('calendar.dateFrozenMessage') };
  }
  if (day.isPaused) {
    return { title: t('calendar.pausedDay'), message: t('calendar.datePausedMessage') };
  }
  if (day.state === 'Booked Meal' || day.state === 'Partially Booked Meal') {
    return { title: t('calendar.mealSelected'), message: '' };
  }
  return { title: t('calendar.noOrdersOnThisDay'), message: t('calendar.noOrdersOnThisDayMessage') };
}
